import { Decimal } from 'decimal.js'
import { DateTime } from 'luxon'
import striptags from 'striptags'
import { decode } from 'html-entities'

function stripAccents(s: string): string {
  const inChrs = 'àáâãäçèéêëìíîïñòóôõöùúûüýÿÀÁÂÃÄÇÈÉÊËÌÍÎÏÑÒÓÔÕÖÙÚÛÜÝ'
  const outChrs = 'aaaaaceeeeiiiinooooouuuuyyAAAAACEEEEIIIINOOOOOUUUUY'
  const charsRgx = new RegExp('[' + inChrs + ']', 'g')
  const transl: { [key: string]: string } = {}
  for (let i = 0; i < inChrs.length; i++) {
    transl[inChrs[i]] = outChrs[i]
  }
  return s.replace(charsRgx, m => transl[m] || m)
}

function makeUrl(item: any, field = 'id'): string {
  let s = item.name || ''
  s = stripAccents(s.toLowerCase())
  s = s.replace(/[\s:;,.]+/g, '-')
  s = s.replace(/[^0-9a-zA-Z-]/ig, '')
  s = s.replace(/-+/g, '-')
  s = s.replace(/-+$/g, '')
  return item[field] + (item.shopid ? '-' + item.shopid : '') + '-' + s
}

function notPaddedNum(n: string | number, d = 2, s = ',', t = ' '): string {
  n = typeof n === 'string' ? n.replace(',', '.') : n
  return parseFloat(new Decimal(isNaN(parseFloat(n as string)) ? 0 : n).toFixed(d).toString()).toString().replace('.', s).replace(/\B(?=(\d{3})+(?!\d))/g, t)
}

function num(n: string | number, d = 2, s = ',', t = ' '): string {
  n = typeof n === 'string' ? n.replace(',', '.') : n
  return new Decimal(isNaN(parseFloat(n as string)) ? 0 : n).toFixed(d).replace('.', s).replace(/\B(?=(\d{3})+(?!\d))/g, t)
}

function getCurrencySymbol(val?: string): string {
  const symbols: { [key: string]: string } = {
    'EUR': '€',
    'USD': '$',
    'AUD': '$',
    'BRL': 'R$',
    'CAD': '$',
    'CRC': '₡',
    'CZK': 'Kč',
    'DKK': 'kr',
    'HKD': '$',
    'HUF': 'Ft',
    'ILS': '₪',
    'JPY': '¥',
    'MYR': 'RM',
    'MXN': '$',
    'NOK': 'kr',
    'NZD': '$',
    'PHP': '₱',
    'PLN': 'zł',
    'GBP': '£',
    'RUB': '₽',
    'SGD': '$',
    'SEK': 'kr',
    'CHF': 'CHF',
    'TWD': 'NT$',
    'THB': '฿'
  }
  if (!val) {
    val = 'EUR' // default to euros
  }
  return symbols[val] || val
}

export default defineNuxtPlugin((nuxtApp) => {
  const store = nuxtApp.$store as any
  const timezone = store.getters.timezone

  // Define global filters as methods
  nuxtApp.vueApp.config.globalProperties.$filters = {
    makeUrl: (shopId: string, locale: string, category?: any, product?: any) => {
      return '/' + locale + (category ? '/categories/' + makeUrl(category) : '') + (product ? '/products/' + makeUrl(product, 'source_id') : '')
    },

    makePageUrl: (shopId: string, locale: string, page: any) => {
      return '/' + locale + '/pages/' + makeUrl(page)
    },

    urlDecode: (val: string) => {
      val = val.replace(/\+/g, '%20')
      const str = val.split('%')
      let cval = str[0]
      for (let i = 1; i < str.length; i++) {
        cval += String.fromCharCode(parseInt(str[i].substring(0, 2), 16)) + str[i].substring(2)
      }
      return cval
    },

    externalLink: (url: string) => {
      if (url && url.match && !url.match(/:\/\//)) {
        return 'http://' + url
      }
      return url
    },

    currency: (amount: number | string, currency?: string, decimals = 2) => {
      const numberFormat = store.state.numberFormat
      if (numberFormat === 'en-US') {
        return getCurrencySymbol(currency) + num(amount, decimals, '.', ',')
      } else {
        return num(amount, decimals, ',', ' ') + ' ' + getCurrencySymbol(currency)
      }
    },

    num: (amount: number | string, precision = 2, forceDecimals = true) => {
      const numberFormat = store.state.numberFormat
      if (forceDecimals) {
        if (numberFormat === 'en-US') {
          return num(amount, precision, '.', ',')
        } else {
          return num(amount, precision, ',', ' ')
        }
      } else {
        if (numberFormat === 'en-US') {
          return notPaddedNum(amount, precision, '.', ',')
        } else {
          return notPaddedNum(amount, precision, ',', ' ')
        }
      }
    },

    marketingname: (product: any) => {
      return (parseInt(product.usemarketingname) && product.marketingname ? product.marketingname : product.name)
    },

    posname: (product: any) => {
      const posname = (parseInt(product.usemarketingname) && product.marketingname ? product.marketingname : product.name)
      const totalLength = posname.length + product.name.length
      return posname !== product.name && totalLength < 50 ? product.name + ' (' + posname + ')' : posname
    },

    currencySymbol: getCurrencySymbol,

    striptags: (val: string) => striptags(val),

    addDashes: (val: string) => val.match(/.{1,4}/g)?.join('-') || val,

    toShortDate: (val: string | number | Date, locale: string) => {
      const sd = new Date(val)
      if (locale === 'en') {
        return (sd.getMonth() + 1) + '/' + sd.getDate()
      } else {
        return sd.getDate() + '.' + (sd.getMonth() + 1) + '.'
      }
    },

    toDate: (val: string | number | Date, locale: string) => {
      const dateFormat = store.state.dateFormat
      if (dateFormat) {
        return DateTime.fromJSDate(new Date(val), { zone: timezone }).toFormat(dateFormat)
      }
      const d = new Date(val)
      if (locale === 'en') {
        return d.getFullYear() + '/' + (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1) + '/' + (d.getDate() + 1 < 10 ? '0' : '') + d.getDate()
      } else {
        return d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear()
      }
    },

    toTime: (val: string | number, locale: string) => {
      const timeFormat = store.state.timeFormat
      if (timeFormat) {
        return DateTime.fromSeconds(Number(val), { zone: timezone }).toFormat(timeFormat)
      }
      const hours12 = (date: Date) => (date.getHours() + 24) % 12 || 12
      const d = new Date(parseInt(val as string))
      if (locale === 'en') {
        return hours12(d) + '.' + (d.getMinutes() < 10 ? '0' + d.getMinutes() : d.getMinutes()) + ' ' + (d.getHours() < 12 ? 'AM' : 'PM')
      } else {
        return d.getHours() + '.' + (d.getMinutes() < 10 ? '0' + d.getMinutes() : d.getMinutes())
      }
    },

    timestampToDate: (val: string | number, locale: string) => {
      if (isNaN(parseInt(val as string))) {
        return ''
      }
      const d = new Date(parseInt(val as string) * 1000)
      const dateFormat = store.getters.dateFormat
      if (dateFormat) {
        return DateTime.fromJSDate(d, { zone: timezone }).toFormat(dateFormat)
      }
      if (locale === 'en') {
        return d.getFullYear() + '/' + (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1) + '/' + (d.getDate() + 1 < 10 ? '0' : '') + d.getDate()
      } else {
        return d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear()
      }
    },

    timestampToTime: (val: string | number, locale: string) => {
      const hours12 = (date: Date) => (date.getHours() + 24) % 12 || 12
      const d = new Date(parseInt(val as string) * 1000)
      const timeFormat = store.getters.timeFormat
      if (timeFormat) {
        return DateTime.fromJSDate(d, { zone: timezone }).toFormat(timeFormat)
      }
      if (locale === 'en') {
        return hours12(d) + '.' + (d.getMinutes() < 10 ? '0' + d.getMinutes() : d.getMinutes()) + ' ' + (d.getHours() < 12 ? 'AM' : 'PM')
      } else {
        return d.getHours() + '.' + (d.getMinutes() < 10 ? '0' + d.getMinutes() : d.getMinutes())
      }
    },

    htmlentities: (val: string) => decode(val),

    truncate: (text: string, length = 30, clamp = '...') => {
      if (text.length <= length) return text

      let tcText = text.slice(0, length - clamp.length)
      let last = tcText.length - 1

      while (last > 0 && tcText[last] !== ' ' && tcText[last] !== clamp[0]) last -= 1

      // Fix for case when text dont have any `space`
      last = last || length - clamp.length

      tcText = tcText.slice(0, last)

      return tcText + clamp
    }
  }
}) 